/**
 * Provider del tema e selettore a tre stati.
 *
 * La scelta si scrive come attributo `data-tema` sull'elemento radice: i token
 * di `styles/tema.css` sono definiti tre volte — su `:root`, sotto
 * `prefers-color-scheme: dark` e sotto `:root[data-tema="scuro"]` — cosi' il
 * tema di sistema funziona senza JavaScript e la scelta esplicita vince in
 * entrambe le direzioni.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { ContestoTema, ETICHETTE_TEMA, TEMI } from "./contesto";
import type { Tema, ValoreTema } from "./contesto";
import { useTema } from "./contesto";
import { IconaChiaro, IconaScuro, IconaSistema } from "../components/Icone";

const CHIAVE_TEMA = "trasparenza.backoffice.tema";

function temaMemorizzato(): Tema {
  try {
    const salvato = window.localStorage.getItem(CHIAVE_TEMA);
    return TEMI.includes(salvato as Tema) ? (salvato as Tema) : "sistema";
  } catch {
    return "sistema";
  }
}

export function FornitoreTema({ children }: { children: ReactNode }) {
  const [tema, setTema] = useState<Tema>(temaMemorizzato);

  useEffect(() => {
    const radice = document.documentElement;
    if (tema === "sistema") radice.removeAttribute("data-tema");
    else radice.setAttribute("data-tema", tema);
    try {
      window.localStorage.setItem(CHIAVE_TEMA, tema);
    } catch {
      // Preferenza non memorizzabile: resta valida per questa sessione.
    }
  }, [tema]);

  const imposta = useCallback((nuovo: Tema) => setTema(nuovo), []);
  const valore = useMemo<ValoreTema>(() => ({ tema, imposta }), [tema, imposta]);

  return <ContestoTema.Provider value={valore}>{children}</ContestoTema.Provider>;
}

const ICONE: Record<Tema, typeof IconaChiaro> = {
  sistema: IconaSistema,
  chiaro: IconaChiaro,
  scuro: IconaScuro,
};

/** Selettore ciclico: sistema → chiaro → scuro → sistema. */
export function SelettoreTema() {
  const { tema, imposta } = useTema();
  const Icona = ICONE[tema];
  const successivo = TEMI[(TEMI.indexOf(tema) + 1) % TEMI.length];

  return (
    <button
      type="button"
      className="bottone bottone--fantasma bottone--piccolo bottone--icona"
      onClick={() => imposta(successivo)}
      title={`${ETICHETTE_TEMA[tema]}. Premere per passare a: ${ETICHETTE_TEMA[successivo].toLowerCase()}.`}
      aria-label={`${ETICHETTE_TEMA[tema]}. Passa a ${ETICHETTE_TEMA[successivo].toLowerCase()}.`}
    >
      <Icona />
    </button>
  );
}
